import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type MultiSelectOption,
} from '@ncoder/ui';
import { DialogActions, FormField, ORG_ADMIN_INPUT_CLASS } from './OrgAdminForm';
import type { OrgRole, OrgServiceCatalogItem } from '@/services/org-admin';

interface RoleFormValues {
  name: string;
}

export interface RoleFormSubmitData {
  name: string;
  // Omitted when editing the seeded Owner role (never changeable) or when left at the default.
  management_level?: 'admin' | 'member';
  capability_keys: string[];
}

export interface RoleFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialRole: OrgRole | null;
  serviceCatalog: OrgServiceCatalogItem[];
  // Only an owner may grant the admin management level to a role.
  canGrantAdmin: boolean;
  isSubmitting: boolean;
  onSubmit: (data: RoleFormSubmitData) => void;
  onClose: () => void;
}

export function RoleFormModal({
  open,
  mode,
  initialRole,
  serviceCatalog,
  canGrantAdmin,
  isSubmitting,
  onSubmit,
  onClose,
}: RoleFormModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<RoleFormValues>({ defaultValues: { name: '' }, mode: 'onChange' });

  const [selectedServiceSlugs, setSelectedServiceSlugs] = useState<string[]>(
    initialRole?.capabilities.map((capability) => capability.key) ?? [],
  );
  // Tracks whether the user has interacted with the services field, so "select at least one" is
  // shown as a consequence of their editing rather than as an accusation on open. The old version
  // rendered the error immediately for every freshly opened Create dialog.
  const [servicesTouched, setServicesTouched] = useState(false);

  // The seeded Owner role's management_level can never be changed here (there's
  // exactly one, and "owner" is never a settable value via this form).
  const isOwnerRole = initialRole?.management_level === 'owner';
  const [managementLevel, setManagementLevel] = useState<'admin' | 'member'>(
    initialRole?.management_level === 'admin' ? 'admin' : 'member',
  );

  useEffect(() => {
    if (!open) return;
    reset({ name: initialRole?.name ?? '' });
    setSelectedServiceSlugs(initialRole?.capabilities.map((capability) => capability.key) ?? []);
    setManagementLevel(initialRole?.management_level === 'admin' ? 'admin' : 'member');
    setServicesTouched(false);
  }, [open, initialRole, reset]);

  const serviceOptions: MultiSelectOption[] = serviceCatalog.map((service) => ({
    label: service.label,
    value: service.key,
  }));

  const hasNoServices = selectedServiceSlugs.length === 0;

  const submit = handleSubmit((data) => {
    // Guard here too: the button is disabled, but Enter-to-submit reaches this path directly.
    if (hasNoServices) {
      setServicesTouched(true);
      return;
    }
    onSubmit({
      name: data.name,
      management_level: isOwnerRole ? undefined : managementLevel,
      capability_keys: selectedServiceSlugs,
    });
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {mode === 'edit' ? `Edit “${initialRole?.name ?? 'role'}”` : 'Create role'}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            A role bundles the modules its holders may use, plus how much admin authority they
            carry.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col gap-5" noValidate>
          <DialogMain className="space-y-4">
            <FormField label="Name" required error={errors.name?.message}>
              {(fieldProps) => (
                <input
                  {...fieldProps}
                  autoFocus
                  autoComplete="off"
                  placeholder="e.g. Analyst"
                  disabled={isSubmitting}
                  className={ORG_ADMIN_INPUT_CLASS}
                  {...register('name', {
                    required: 'Give the role a name',
                    validate: (value) => value.trim().length > 0 || 'Give the role a name',
                  })}
                />
              )}
            </FormField>

            <FormField
              label="Management level"
              hint={
                isOwnerRole
                  ? undefined
                  : canGrantAdmin
                    ? 'Admins can invite members and assign member-level roles.'
                    : 'Only an owner can grant admin authority.'
              }
            >
              {({ id, ...fieldProps }) =>
                isOwnerRole ? (
                  <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[13px] text-muted-foreground">
                    Owner — every org has exactly one Owner role, and its level can't be changed.
                  </p>
                ) : (
                  <Select
                    value={managementLevel}
                    onValueChange={(value) => setManagementLevel(value as 'admin' | 'member')}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id={id} {...fieldProps} className="h-9 text-[13px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="member" className="text-[13px]">
                        Member — no admin authority
                      </SelectItem>
                      {canGrantAdmin && (
                        <SelectItem value="admin" className="text-[13px]">
                          Admin — can invite and assign roles
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )
              }
            </FormField>

            {/*
              "Modules", not "Services", and the rename is the point.

              This field grants `capability_keys`, which are literally `module:<slug>` values, and the
              sidebar calls the things they unlock modules. But the field was labelled "Services" —
              the same word the Usage tab uses for a PRODUCT FEATURE in "Usage by service". Two
              adjacent tabs, one word, two entirely different lists: a role's grant bundle and a
              feature that burned tokens. "Module" is what the keys and the navigation already say.
            */}
            <FormField
              label="Modules"
              required
              hint={
                serviceOptions.length === 0
                  ? 'No modules are available for this org yet.'
                  : `Holders get access to exactly what you pick here — ${selectedServiceSlugs.length} of ${serviceOptions.length} selected.`
              }
              error={servicesTouched && hasNoServices ? 'Pick at least one module' : undefined}
            >
              {({ id, ...fieldProps }) => (
                <div id={id} {...fieldProps}>
                  <MultiSelect
                    // Remounts the uncontrolled MultiSelect when the dialog switches roles, so it
                    // does not keep the previous role's selection.
                    key={initialRole?.id ?? 'create'}
                    options={serviceOptions}
                    onValueChange={(values) => {
                      setSelectedServiceSlugs(values);
                      setServicesTouched(true);
                    }}
                    defaultValue={initialRole?.capabilities.map((capability) => capability.key) ?? []}
                    placeholder="Select modules…"
                    searchable
                    modalPopover
                  />
                </div>
              )}
            </FormField>
          </DialogMain>

          <DialogFooter>
            <DialogActions
              onCancel={onClose}
              submitLabel={mode === 'edit' ? 'Save changes' : 'Create role'}
              pendingLabel="Saving…"
              isSubmitting={isSubmitting}
              isDisabled={!isValid || hasNoServices}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
